import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";

import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import RunJobAsComponent from "../../../utils/RunJobAsComponent";
import { setPrevState } from "../../../../../../util/common";
import TableAsTarget from "./TableAsTarget";
import BrowserAsTarget from "./BrowserAsTarget";
import BrowseInput from "./BrowseInput";
import SmallToolbar from "../../../../../Common/SmallToolbar";
import RegexHelp from "./RegexHelp";
import { JOB_PURPOSE_CONVERSION, JOB_PURPOSE_EXCHANGE } from "../../../utils/JobData";

const sqlJobs = ["snowflake", "salesforce", "veevavault", "mysql"];
const browserAsTargets = ["s3", "sftp", "azure"];

const DestinationForm = ({
  exchangeJobState,
  setExchangeJobState,
  previousState,
  participantsExcludeVisitor,
  handleUserChange,
  handleRunUnderMyName,
  accessRole,
  jobPurpose,
  setRunTimeState,
  runTimeState,
  loadSchemasClusters,
}) => {
  const { Job_id } = useParams();

  const { destination, locationList, source, sourceType, sourcePath, singleFile } = exchangeJobState;

  const [openRegexHelp, setOpenRegexHelp] = useState(false);

  const destinationType = useMemo(
    () => locationList.find(({ Stage_id }) => Stage_id === destination)?.Detail?.type?.toLowerCase() || "",
    [destination, locationList]
  );

  const allowRename = useMemo(() => {
    if (sourceType?.toLowerCase() === "s3" && (destinationType === "s3" || destinationType === "sftp")) return true;
    if (destinationType === "s3" && (sourceType?.toLowerCase() === "sftp" || sourceType?.toLowerCase() === "azure"))
      return true;

    return false;
  }, [destinationType, sourceType]);

  const allowUnzip = useMemo(() => {
    return (
      (sourceType?.toLowerCase() === "s3" ||
        sourceType?.toLowerCase() === "sftp" ||
        sourceType?.toLowerCase() === "azure") &&
      (destinationType?.toLowerCase() === "s3" ||
        destinationType?.toLowerCase() === "sftp" ||
        destinationType?.toLowerCase() === "azure")
    );
  }, [destinationType, sourceType]);

  const $destinationList = useMemo(() => {
    if (!locationList.length) return [];

    if (jobPurpose === JOB_PURPOSE_CONVERSION) {
      return locationList.filter(({ Detail }) => ["s3", "azure"].includes(Detail?.type?.toLowerCase()));
    } else {
      // add acceptable types based on sourceType and source
      const lowerSourceType = sourceType.toLowerCase();
      const acceptableTypes = new Set(["s3"]);

      if (source === "SNOWFLAKE") {
        acceptableTypes.add("salesforce");
        acceptableTypes.add("veevavault");
      }
      if (lowerSourceType !== "snowflake") acceptableTypes.add("snowflake");
      if (lowerSourceType === "s3")
        ["mysql", "sftp", "salesforce", "veevavault"].forEach((type) => acceptableTypes.add(type));

      let filteredList = locationList.filter(({ Detail, Stage_id }) => {
        const detailType = Detail?.type?.toLowerCase();

        if (!acceptableTypes.has(detailType)) return false;
        if (sqlJobs.includes(lowerSourceType) || lowerSourceType === "") return detailType !== "snowflake";
        if (lowerSourceType === "s3") {
          return detailType === "snowflake" || ["s3", "mysql", "sftp", "salesforce", "veevavault"].includes(detailType);
        }
        if (lowerSourceType !== "s3" && detailType === "snowflake") return Stage_id === "SNOWFLAKE";
        return true;
      });

      return filteredList;
    }
  }, [source, locationList, jobPurpose, sourceType]);

  useEffect(() => {
    if (!$destinationList.length || !destination) return;

    if (!$destinationList.find(({ Stage_id }) => Stage_id === destination)) {
      setPrevState(setExchangeJobState, {
        destination: "",
        targetPath: "",
      });
    }
  }, [destination, $destinationList, setExchangeJobState]);

  const $hideProperties = useMemo(
    () => !!(["snowflake", "mysql"]?.includes(destinationType) && sourcePath && sourcePath.split(",/").length > 1),
    [destinationType, sourcePath]
  );

  const handleConnectionChange = (e) => {
    const selectedLocation = $destinationList.find((location) => location.Stage_id === e.target.value).Detail;

    loadSchemasClusters(selectedLocation.type?.toLowerCase(), e.target.value);
    setExchangeJobState((prev) => {
      return {
        ...prev,
        ...(e.target.value !== prev.destination
          ? {
              targetPath: "",
              desinationContainer: "",
            }
          : {}),
        ...(e.target.value !== prev.destination && prev.sourceType !== "snowflake" && prev.source !== "SNOWFLAKE"
          ? { schemaName: "", tableName: "", externalIdFieldName: "" }
          : {}),
        destination: e.target.value,
        fileType: selectedLocation.type?.toLowerCase() === "mysql" ? "csv" : prev.fileType,
        desinationContainer: selectedLocation.container,
        isFieldsConfigured: e.target.value !== previousState.destination ? false : exchangeJobState.isFieldsConfigured,
        ...(destination === "SNOWFLAKE" ? { targetPath: "", desinationContainer: "" } : {}),
      };
    });
  };

  return (
    <Grid container direction="row" spacing={0}>
      {browserAsTargets.includes(destinationType) && <SmallToolbar>Export location</SmallToolbar>}
      <Grid size={12}>
        <FormControl
          sx={{
            width: { xs: "100%", sm: "300px" },
            m: 1,
            ml: 0,
          }}
          error={$hideProperties}
        >
          <InputLabel required>Connection</InputLabel>
          <Select
            label="Connection"
            value={destination}
            onChange={handleConnectionChange}
            required
            MenuProps={{ sx: { height: "320px", maxWidth: "300px" } }}
          >
            {$destinationList.map((option) => (
              <MenuItem
                key={option.Stage_id}
                value={option.Stage_id}
                sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
                disabled={option.Stage_id === "SNOWFLAKE" && !source && source === "SNOWFLAKE"}
              >
                {option.Name}
                {option?.IsDataLake === 1 &&
                  (option?.Detail.type.toLowerCase() === "s3" ? (
                    <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                  ) : (
                    <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                  ))}
              </MenuItem>
            ))}
          </Select>
          {$hideProperties && (
            <FormHelperText>Please select just 1 file to create table in the database</FormHelperText>
          )}
        </FormControl>
        {destination?.toString()?.toLowerCase() === "snowflake" && (
          <RunJobAsComponent
            participants={participantsExcludeVisitor}
            selectedUser={exchangeJobState.runJobAsUserId}
            handleChange={handleUserChange}
            handleRunUnderMyName={handleRunUnderMyName}
            editable={accessRole === 1 && participantsExcludeVisitor.length > 0}
            error={Job_id && !exchangeJobState.runJobAsUserId}
            nonEditableValue={!exchangeJobState.runJobAsUserId ? "" : exchangeJobState.runJobAsUserFullName}
            sx={{
              width: { xs: "100%", sm: "300px" },
              m: 1,
              ml: 0,
            }}
            fullWidth
          />
        )}
        {destinationType.toLowerCase() === "snowflake" && destination?.toString()?.toLowerCase() !== "snowflake" && (
          <FormControl required sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }} variant="outlined">
            <InputLabel>Compute Cluster</InputLabel>
            <Select
              value={runTimeState.cluster}
              onChange={(e) => setPrevState(setRunTimeState, { cluster: e.target.value, clusterError: false })}
              label="Compute Cluster"
              MenuProps={{ sx: { height: "220px" } }}
              error={runTimeState?.clusterError}
            >
              {(runTimeState?.clusterList || [])?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!["snowflake", "mysql", "salesforce", "veevavault", ""].includes(destinationType) && (
          <>
            <BrowseInput
              exchangeJobState={exchangeJobState}
              setExchangeJobState={setExchangeJobState}
              $destinationList={$destinationList}
              jobPurpose={jobPurpose}
              destinationType={destinationType}
            />
            {allowRename && jobPurpose === JOB_PURPOSE_EXCHANGE && (
              <>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exchangeJobState.renameFileRegex}
                          onChange={(e) =>
                            setPrevState(setExchangeJobState, { renameFileRegex: !exchangeJobState.renameFileRegex })
                          }
                        />
                      }
                      sx={{ mr: 1 }}
                      label="Rename files using REGEX"
                    />
                  </FormGroup>
                  <Tooltip title="Help" placement="top" arrow>
                    <IconButton color="info" size="small" onClick={() => setOpenRegexHelp((prev) => !prev)}>
                      <HelpOutlineOutlinedIcon sx={{ cursor: "pointer", fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {exchangeJobState.renameFileRegex && (
                  <>
                    <FormControl sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
                      <TextField
                        value={exchangeJobState.regexMatchPattern}
                        required
                        label="Match Pattern"
                        inputProps={{ maxLength: 255 }}
                        onChange={(e) => {
                          if (e.target.value === "") setPrevState(setExchangeJobState, { regexMatchPattern: "" });
                          else setPrevState(setExchangeJobState, { regexMatchPattern: e.target.value });
                        }}
                        helperText={`Enter a valid regex pattern.`}
                      />
                    </FormControl>
                    <FormControl sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
                      <TextField
                        value={exchangeJobState.regexReplacePattern}
                        required
                        label="Replace pattern"
                        inputProps={{ maxLength: 255 }}
                        onChange={(e) => {
                          if (e.target.value === "") setPrevState(setExchangeJobState, { regexReplacePattern: "" });
                          else setPrevState(setExchangeJobState, { regexReplacePattern: e.target.value });
                        }}
                      />
                    </FormControl>
                  </>
                )}
              </>
            )}

            {openRegexHelp && <RegexHelp onClose={() => setOpenRegexHelp(false)}></RegexHelp>}

            {allowUnzip && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exchangeJobState.unzipFiles}
                        onChange={(e) =>
                          setPrevState(setExchangeJobState, { unzipFiles: !exchangeJobState.unzipFiles })
                        }
                      />
                    }
                    sx={{ mr: 1 }}
                    label="Unzip Files"
                  />
                </FormGroup>
              </Box>
            )}
          </>
        )}
      </Grid>
      {sqlJobs.includes(sourceType.toLowerCase()) && browserAsTargets.includes(destinationType) && (
        <BrowserAsTarget
          exchangeJobState={exchangeJobState}
          setExchangeJobState={setExchangeJobState}
          previousState={previousState}
        />
      )}
      {((!$hideProperties && ["snowflake", "mysql"].includes(destinationType)) ||
        ["salesforce", "veevavault"].includes(destinationType)) && (
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography
                sx={{
                  fontWeight: "bold",
                }}
              >
                Destination Table Properties -
              </Typography>
            </Grid>
            <TableAsTarget
              exchangeJobState={exchangeJobState}
              setExchangeJobState={setExchangeJobState}
              destinationType={destinationType}
              previousState={previousState}
            />
          </Grid>
        </Grid>
      )}
      {["MYSQL", "SNOWFLAKE"].includes(sourceType.toUpperCase()) &&
        destinationType.toUpperCase() === "S3" &&
        !singleFile && (
          <Grid>
            <Alert
              variant="outlined"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ mt: 1.5, pt: 0, pb: 0, borderRadius: 20 }}
            >
              {`Exporting data from ${
                sourceType.toUpperCase() === "MYSQL" ? `AURORA ${sourceType.toUpperCase()}` : sourceType.toUpperCase()
              } to ${destinationType.toUpperCase()} may generate multiple files.`}
            </Alert>
          </Grid>
        )}
    </Grid>
  );
};

export default React.memo(DestinationForm);
